import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Mail, Loader2, CheckCircle2, XCircle, X, RefreshCw, Send, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtFecha = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
  } catch { return iso; }
};
const fmtDT = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return iso; }
};

export default function CapacitadorNotificaciones() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // { row, envios }
  const [detailLoading, setDetailLoading] = useState(false);
  const [reenvioBusy, setReenvioBusy] = useState(null); // sesion_id
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const openPreview = async (row) => {
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/capacitador/sesiones/${row.sesion_id}/email-preview`, { headers });
      setPreview({ ...res.data, _sesion: row });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar preview');
    }
    setPreviewLoading(false);
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/capacitador/notificaciones-historial`, { headers });
      setRows(res.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar'); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openDetail = async (row) => {
    setDetail({ row, envios: [] });
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/capacitador/notificaciones-historial/${row.sesion_id}`, { headers });
      setDetail({ row, envios: res.data || [] });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar detalle'); }
    setDetailLoading(false);
  };

  const reenviar = async (row, soloFallidos = false) => {
    setReenvioBusy(row.sesion_id + (soloFallidos ? ':fallidos' : ''));
    try {
      const url = `${API}/capacitador/sesiones/${row.sesion_id}/cerrar-nomina?force=true${soloFallidos ? '&solo_fallidos=true' : ''}`;
      const res = await axios.post(url, {}, { headers });
      const d = res.data || {};
      toast.success(d.message || 'Reenviado');
      fetchRows();
      if (detail && detail.row.sesion_id === row.sesion_id) openDetail(row);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al reenviar'); }
    setReenvioBusy(null);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="cap-notificaciones">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Mail className="w-6 h-6 text-teal-600" />Historial de Notificaciones</h1>
          <p className="text-xs text-slate-500 mt-0.5">Sesiones cuya nómina ya fue cerrada y notificada por email. Puedes revisar el detalle y reenviar.</p>
        </div>
        <button onClick={fetchRows} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">
          <RefreshCw className="w-3.5 h-3.5" />Actualizar
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Mail className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">Aún no has cerrado nóminas ni enviado notificaciones.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Capacitación</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Fecha</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Modalidad</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Inscritos</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Enviados</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Fallidos</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Notificado</th>
                <th className="text-right px-4 py-3 text-slate-500 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.sesion_id} className="border-b border-slate-100" data-testid={`row-notif-${r.sesion_id}`}>
                  <td className="px-4 py-3">{r.capacitacion_nombre}</td>
                  <td className="px-4 py-3 text-xs">{fmtFecha(r.fecha)} · {r.hora_inicio || ''}</td>
                  <td className="px-4 py-3 text-xs">{(r.modalidad || '').toString()}</td>
                  <td className="px-4 py-3 text-xs">{r.inscritos}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                      <CheckCircle2 className="w-3 h-3" />{r.enviados}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {r.fallidos > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600">
                        <XCircle className="w-3 h-3" />{r.fallidos}
                      </span>
                    ) : <span className="text-xs text-slate-400">0</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDT(r.notificacion_enviada_at || r.nomina_cerrada_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1.5">
                      <button onClick={() => openPreview(r)} disabled={previewLoading}
                        className="inline-flex items-center gap-1 h-8 px-3 text-xs border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-50"
                        data-testid={`btn-preview-${r.sesion_id}`}
                        title="Vista previa del correo con los datos reales">
                        <Eye className="w-3 h-3" />Preview
                      </button>
                      <button onClick={() => openDetail(r)}
                        className="inline-flex items-center gap-1 h-8 px-3 text-xs border border-slate-200 rounded-md hover:bg-slate-50"
                        data-testid={`btn-detalle-${r.sesion_id}`}>
                        Ver detalle
                      </button>
                      <button onClick={() => reenviar(r)} disabled={reenvioBusy === r.sesion_id}
                        className="inline-flex items-center gap-1 h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50"
                        data-testid={`btn-reenviar-${r.sesion_id}`}
                        title="Reenvía a todos los inscritos con email">
                        {reenvioBusy === r.sesion_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        Reenviar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Detalle */}
      {detail && createPortal(
        <div style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="modal-notif-detalle">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{detail.row.capacitacion_nombre}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{fmtFecha(detail.row.fecha)} · {detail.row.hora_inicio || ''}</p>
              </div>
              <button onClick={() => setDetail(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {detailLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
              ) : detail.envios.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">No hay envíos registrados aún.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 sticky top-0 bg-white">
                      <th className="text-left px-2 py-2 text-slate-500 font-medium">Trabajador</th>
                      <th className="text-left px-2 py-2 text-slate-500 font-medium">Email</th>
                      <th className="text-left px-2 py-2 text-slate-500 font-medium">Estado</th>
                      <th className="text-left px-2 py-2 text-slate-500 font-medium">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.envios.map(e => (
                      <tr key={e.id} className="border-b border-slate-100">
                        <td className="px-2 py-2 text-xs">{e.nombre_trabajador || <span className="text-slate-300">—</span>}<br/><span className="text-[10px] text-slate-400 font-mono">{e.rut_trabajador}</span></td>
                        <td className="px-2 py-2 text-xs font-mono">{e.email}</td>
                        <td className="px-2 py-2">
                          {e.estado === 'enviado'
                            ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="w-3 h-3" />Enviado</span>
                            : <div>
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"><XCircle className="w-3 h-3" />Fallido</span>
                                {e.error && <div className="text-[10px] text-red-500 mt-0.5 max-w-[280px]">{e.error}</div>}
                              </div>}
                        </td>
                        <td className="px-2 py-2 text-[11px] text-slate-500">{fmtDT(e.enviado_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between text-xs text-slate-600 gap-2 flex-wrap">
              <span>Total inscritos: {detail.row.inscritos} · Enviados: {detail.row.enviados} · Fallidos: {detail.row.fallidos}</span>
              <div className="flex items-center gap-2">
                {detail.row.fallidos > 0 && (
                  <button onClick={() => reenviar(detail.row, true)} disabled={reenvioBusy !== null}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50"
                    data-testid="btn-reenviar-solo-fallidos"
                    title={`Reenvía solo a los ${detail.row.fallidos} destinatarios que fallaron`}>
                    {reenvioBusy === detail.row.sesion_id + ':fallidos' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    Reenviar solo a fallidos ({detail.row.fallidos})
                  </button>
                )}
                <button onClick={() => reenviar(detail.row)} disabled={reenvioBusy !== null}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50">
                  {reenvioBusy === detail.row.sesion_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  Reenviar a todos
                </button>
              </div>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Vista previa del correo */}
      {preview && createPortal(
        <div style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          onClick={() => setPreview(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
            onClick={e => e.stopPropagation()} data-testid="modal-preview-email-hist">
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Vista previa del correo</h3>
                <p className="text-xs text-slate-500 mt-1">
                  De: <span className="font-mono">{preview.sender}</span> · Asunto: <span className="italic">{preview.subject}</span>
                </p>
              </div>
              <button onClick={() => setPreview(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-3 border-b border-slate-100 bg-slate-50 text-xs text-slate-700 flex flex-wrap gap-3">
              <span><strong>{preview.total}</strong> inscritos</span>
              <span className="text-emerald-700"><strong>{preview.con_email}</strong> con email</span>
              {preview.sin_email > 0 && <span className="text-red-600"><strong>{preview.sin_email}</strong> sin email</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 flex-1 overflow-hidden">
              <div className="md:col-span-2 overflow-y-auto bg-slate-100 p-3 md:p-4 min-h-[240px]">
                <iframe title="Preview email" srcDoc={preview.html}
                  className="w-full h-full min-h-[420px] md:min-h-[500px] bg-white rounded-lg border border-slate-200 shadow-sm" />
              </div>
              <div className="md:col-span-1 border-t md:border-t-0 md:border-l border-slate-200 overflow-y-auto max-h-[240px] md:max-h-none">
                <div className="sticky top-0 bg-slate-50 border-b border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">
                  Destinatarios ({preview.destinatarios.length})
                </div>
                <ul className="text-xs divide-y divide-slate-100">
                  {preview.destinatarios.map((d, i) => (
                    <li key={i} className={`px-3 py-2 ${!d.tiene_email ? 'bg-red-50' : ''}`}>
                      <div className="font-medium text-slate-800">{d.nombre || <span className="text-slate-300">—</span>}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{d.rut}</div>
                      {d.tiene_email
                        ? <div className="text-[11px] text-slate-600 truncate">{d.email}</div>
                        : <div className="text-[10px] text-red-600 font-semibold">Sin email — no recibirá</div>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button type="button" onClick={() => setPreview(null)}
                className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">
                Cerrar preview
              </button>
              <button type="button" disabled={reenvioBusy !== null}
                onClick={() => { const s = preview._sesion; setPreview(null); reenviar(s); }}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold disabled:opacity-50">
                <Send className="w-4 h-4" />Reenviar a todos
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
